import styled, { css } from 'styled-components';
import React from 'react';
import { Caption } from '../Typography/Typography';
import BaseInput, { BaseInputProps } from '../Input/Input';
import { rem } from 'polished';
import Box from '../Box/Box';
import Tooltip from '../Tooltip/Tooltip';
import InfoIcon from '../../images/icons/info.svg';
import { theme } from '../../../config/theme';
import { MinusIcon } from '../../icons/MinusIcon';
import { PlusIcon } from '../../icons/PlusIcon';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import './styles.css';

const LabelContext = React.createContext({ id: null, disabled: false });

function useLabelContext() {
    const context = React.useContext(LabelContext);
    if (!context) {
        throw new Error(
            `Label compound components cannot be rendered outside the Label component`
        );
    }
    return context;
}

export const StyledLabel = styled(Box)`
    display: block;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
            cursor: not-allowed;
        `}

    ::placeholder {
        color: ${({ theme }) => theme.colors.neutral['1400']};
    }
`;

interface FieldInterface extends React.FC<{ id: string; disabled?: boolean }> {
    Input: typeof Input;
    Textarea: typeof Input;
    Label: React.FC;
    Hint: React.FC;
    ErrorMessage: React.FC;
    QuantityInput: typeof Input;
    InputRange: React.FC;
}

const Field: FieldInterface = ({ id, disabled, width, children }) => (
    <LabelContext.Provider value={{ id, disabled }}>
        <StyledLabel as="label" htmlFor={id} disabled={disabled} width={width}>
            {children}
        </StyledLabel>
    </LabelContext.Provider>
);

const Input = React.forwardRef((props: BaseInputProps, ref) => {
    const { id, disabled } = useLabelContext();
    return (
        <BaseInput id={id} disabled={disabled} name={id} ref={ref} {...props} />
    );
});

const QuantityInput = React.forwardRef(
    ({ decrement, increment, name, ...props }: BaseInputProps, ref) => {
        const { id, disabled } = useLabelContext();

        return (
            <Box display="flex" alignItems="center" gap={rem(2)}>
                <Box
                    as="button"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="sm"
                    backgroundColor="primary.1800"
                    color="white"
                    width={rem(32)}
                    height={rem(40)}
                    cursor="pointer"
                    css={css`
                        &:active {
                            transform: scale(0.96);
                        }
                    `}
                    onClick={() => {
                        decrement();
                    }}
                >
                    <MinusIcon />
                </Box>
                <Box flex={1}>
                    <BaseInput
                        type="text"
                        isQuantity
                        id={id}
                        disabled={disabled}
                        name={name}
                        ref={ref}
                        width="100%"
                        {...props}
                    />
                </Box>
                <Box
                    as="button"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="sm"
                    backgroundColor="primary.1800"
                    color="white"
                    width={rem(32)}
                    height={rem(40)}
                    cursor="pointer"
                    css={css`
                        &:active {
                            transform: scale(0.96);
                        }
                    `}
                    onClick={increment}
                >
                    <PlusIcon />
                </Box>
            </Box>
        );
    }
);

const InputRange = React.forwardRef((props, ref) => {
    return (
        <Box
            my={rem(8)}
            as={RangeSlider}
            className="single-thumb"
            defaultValue={[0, 100]}
            thumbsDisabled={[true, false]}
            rangeSlideDisabled={true}
            css={css`
                background-color: ${theme.colors.neutral['100']};
                & .range-slider__range {
                    /* CSS for range */
                    background-color: ${theme.colors.primary['1800']};
                }
                & .range-slider__thumb {
                    /* CSS for thumbs */
                    background-color: ${theme.colors.primary['1800']};
                    width: 16px;
                    height: 16px;
                }
            `}
            {...props}
        />
    );
});

const Label = ({ tooltipLabel = '', ...props }) => {
    return (
        <Box
            as={Caption}
            display="flex"
            alignItems="center"
            gap={rem(4)}
            mb={rem(4)}
            {...props}
        >
            {props.children}
            {tooltipLabel ? (
                <Tooltip
                    placement={['top']}
                    content={<Tooltip.Content>{tooltipLabel}</Tooltip.Content>}
                >
                    <InfoIcon />
                </Tooltip>
            ) : null}
        </Box>
    );
};

const Textarea = React.forwardRef(
    (props: BaseInputProps & JSX.IntrinsicElements['textarea'], ref) => {
        const { id, disabled } = useLabelContext();
        return (
            <BaseInput
                as="textarea"
                id={id}
                name={id}
                disabled={disabled}
                resize="none"
                minHeight={rem(86)}
                ref={ref}
                {...props}
            />
        );
    }
);

const Hint = (props) => (
    <Box
        as={Caption}
        display="block"
        color="neutral.1400"
        mt={rem(4)}
        {...props}
    />
);

const ErrorMessage = (props) => (
    <Caption color="error.700" mt={rem(4)} {...props} />
);

Field.Input = Input;
Field.InputRange = InputRange;
Field.QuantityInput = QuantityInput;
Field.Textarea = Textarea;
Field.Label = Label;
Field.Hint = Hint;
Field.ErrorMessage = ErrorMessage;

export default Field;
